// Install detection — which library titles are installed on which station (F-CAT-03; spec/10 §7.1, §7.2;
// spec/12 §6; spec/13 §3).
//
//   PUT  /v1/station/installed                                              station-authenticated
//   GET  /v1/station/library/availability                                   station-authenticated
//   GET  /v1/organizations/{o}/venues/{v}/stations/{stationId}/installed    library:read
//   GET  /v1/organizations/{o}/venues/{v}/library/installs                  library:read     the titles × stations matrix
//   GET  /v1/organizations/{o}/venues/{v}/library/detected                  library:read     found on PCs, not in the library
//   POST /v1/organizations/{o}/venues/{v}/library/rescan                    stations:manage  `station.library.rescan` per station
//   POST /v1/organizations/{o}/venues/{v}/stations/{s}/apps/{entryId}/install  library:manage  `station.app.install` on one PC
//   POST /v1/organizations/{o}/venues/{v}/library/{entryId}/install         library:manage   …on every PC missing it
//
// The station scans its disks for the titles its signed library offers — Steam's app manifests, Epic's
// manifests, Riot's product settings, the uninstall registry, the Store's package folders, and the
// recipe's resolved exe/shortcut paths (spec/10 §7.1) — on library sync, on boot and every ten minutes.
// The kiosk shows only what is installed here; the rest is "On other PCs". The station reports the
// result here **only when it changes** (never a heartbeat: presence and health stay realtime, spec/09
// §5.3), the server keeps the latest report per station, and:
//
//   - the station pulls, with its library, which *other* stations of its venue have each title
//     (`availability`), so the kiosk can say "On PC 4 and PC 12" — only its own venue's stations, never
//     another venue's;
//   - the venue console's station sheet reads "Installed: N of M library titles" (`installed`);
//   - the console's library area shows the titles × stations matrix (`library/installs`) and what the PCs have
//     that the library does not (`library/detected`: Steam app manifests, Epic manifests and the Ubisoft / EA /
//     Battle.net uninstall entries no library entry matched), and can ask stations to rescan now.
import { createRoute } from '@hono/zod-openapi';
import { z } from "../zod.js";
import { CatalogueRequestIdSchema, GameIdSchema, LibraryEntryIdSchema, OrganizationIdSchema, StationClassIdSchema, StationIdSchema, VenueIdSchema, ZoneIdSchema, } from "../ids/schema.js";
import { commonProblemResponses, JSON_CONTENT_TYPE, jsonResponse, problemResponse } from "../openapi/responses.js";
import { HQ_CATALOG, LIBRARY_MANAGE, LIBRARY_READ, STATIONS_MANAGE } from "../permissions/index.js";
import { IdempotencyKeySchema, IsoDateTimeSchema } from "../primitives/scalars.js";
import { VenueScopeParamsSchema } from "../primitives/scope.js";
import { PERMISSION_EXTENSION } from "./audit.js";
import { StationCommandViewSchema } from "./floor.js";
import { LaunchRecipeLauncherSchema } from "./hq-catalogue.js";
const STATION_AUTHENTICATION = 'Station-authenticated: `Authorization: Bearer <device token>` (spec/07 §5); a user session, a member or ' +
    'a player token, an expired token and a revoked device are each refused with `401`.';
/** At most this many titles in one report — well above any venue library (the bundle is the bound). */
export const MAX_INSTALLED_REPORT_ENTRIES = 2000;
/** At most this many titles found on a PC that no library entry matched, in one report. */
export const MAX_UNMATCHED_INSTALLS = 500;
/** An installed version as its source records it (a Steam build id, an Epic version string, a DisplayVersion). */
const InstallVersionSchema = z.string().trim().min(1).max(128);
/**
 * A title's size on the PC's disk, in bytes, as its launcher records it — Steam's `SizeOnDisk`, Epic's `InstallSize` —
 * or absent when the source records none. The console shows it in GB.
 */
const InstallSizeBytesSchema = z.int().nonnegative().max(Number.MAX_SAFE_INTEGER);
/** Where a title found on a PC but not in the library was found (F-CAT-03, spec/10 §7.2). */
export const INSTALL_SOURCES = ['steam', 'epic', 'ubisoft', 'ea', 'battlenet'];
export const InstallSourceSchema = z.enum(INSTALL_SOURCES).openapi('InstallSource', {
    description: 'Where a title installed on a PC was found: a Steam app manifest, an Epic manifest, or the Ubisoft / EA / Battle.net uninstall entries.',
});
/** A title installed on the PC that no library entry matched (F-CAT-03 "Detected, not in library"). */
export const UnmatchedInstallSchema = z
    .object({
    source: InstallSourceSchema,
    /** The source's own id: the Steam app id, the Epic app name, the uninstall key name. */
    sourceId: z.string().trim().min(1).max(128),
    name: z.string().trim().min(1).max(200),
    version: InstallVersionSchema.optional(),
})
    .openapi('UnmatchedInstall', { description: 'A title installed on a station that no library entry matched (F-CAT-03).' });
/** A title that is mid-update on the station: the launcher's own download progress, 0–100. */
export const InstallUpdatingSchema = z
    .object({
    /** Percent complete (0–100), from the launcher's own bytes downloaded / to download; 0 when unknown. */
    progress: z.int().min(0).max(100),
})
    .openapi('InstallUpdating', { description: 'A title updating on the station, with its progress in percent (F-CAT-03).' });
// ---------------------------------------------------------------------------------------------
// Installing apps for players (F-CAT-09, ADR-0045): what a station reports per app
// ---------------------------------------------------------------------------------------------
//
// For every library app whose catalogue entry carries a provisioning recipe, the station says where the app stands in
// its player account — `installed` (there, whoever put it there), `installing` (Install for players is on and it is
// missing: it installs at the next idle moment, never during a session), `failed` (the last attempt failed — `failure`
// says why; the station retries with backoff), `other_account_only` (not in the player account but installed for
// another Windows account on the PC, so players cannot open it), `missing_from_image` (a diskless client: it is installed
// on the image, not on each PC), `not_on_this_pc` (a Store app that is on no account of the PC, so there is nothing to
// register), `queued` (staff asked for it on this PC — ADR-0056 — and it installs as soon as the PC is free). An app it has
// nothing to say about (Install for players off, not on the PC at all, and not asked for) is left out.
//
// `queued` is also what the console reads for a PC that has not received the request yet (offline, or not yet
// connected): the server shows a pending `station.app.install` command as `queued` until the station answers it.
/** Where a provisionable app stands in a station's player account (F-CAT-09, ADR-0045, ADR-0056). */
export const APP_PROVISIONING_STATES = ['installed', 'installing', 'failed', 'other_account_only', 'missing_from_image', 'not_on_this_pc', 'queued'];
export const AppProvisioningStateSchema = z.enum(APP_PROVISIONING_STATES).openapi('AppProvisioningState', {
    description: 'Where an app with a provisioning recipe stands in a station’s player account (F-CAT-09): `installed`, `installing` (it ' +
        'installs at the next idle moment), `failed` (see `failure`; retried with backoff), `other_account_only` (installed for ' +
        'another Windows account only), `missing_from_image` (a diskless client: install it on the image), `not_on_this_pc` (a ' +
        'Store app on no account of the PC), `queued` (staff asked for it on this PC: it installs as soon as the PC is free, ' +
        'never during a session; ADR-0056).',
});
/** Why the last attempt to install an app for players failed (F-CAT-09). */
export const APP_PROVISIONING_FAILURES = [
    'download_failed',
    'host_not_allowed',
    'signature_invalid',
    'signer_mismatch',
    'installer_failed',
    'timed_out',
    'not_found_after_install',
    'register_failed',
    'snapshot_failed',
    'tampered',
    'restore_failed',
    'other',
];
export const AppProvisioningFailureSchema = z.enum(APP_PROVISIONING_FAILURES).openapi('AppProvisioningFailure', {
    description: 'Why installing an app for players failed: the download failed; a redirect left the allowed hosts; the file is unsigned or its ' +
        'signature is not valid; it is signed by someone else; the installer failed or ran too long; it finished but the app is not ' +
        'there; the Store package could not be registered; the installed app could not be snapshotted; it no longer matches its ' +
        'snapshot and could not be checked against it (`tampered`); restoring it from its snapshot failed; or something else. After ' +
        '`snapshot_failed`, `tampered` and `restore_failed` the PC does not offer the app until it is installed again.',
});
/** One app's provisioning state on the station, as it reports it. */
export const StationAppProvisioningSchema = z
    .object({
    entryId: LibraryEntryIdSchema,
    state: AppProvisioningStateSchema,
    /** Why the last attempt failed — present when `state` is `failed`. */
    failure: AppProvisioningFailureSchema.optional(),
    /** A short technical detail for support (an exit code, an HTTP status), never shown to players. */
    detail: z.string().trim().min(1).max(300).optional(),
    /** How many attempts have failed in a row (reset once it is installed). */
    attempts: z.int().min(0).max(10_000).optional(),
    /** When the last attempt ended, RFC 3339 UTC. */
    lastAttemptAt: IsoDateTimeSchema.optional(),
    /** When the station will try again, RFC 3339 UTC — at the first idle moment after it. */
    nextAttemptAt: IsoDateTimeSchema.optional(),
})
    .openapi('StationAppProvisioning', { description: 'Where an app with a provisioning recipe stands in the station’s player account (F-CAT-09).' });
/** One app's provisioning state as the console reads it (nullable fields rather than optional ones). */
export const TitleProvisioningSchema = z
    .object({
    state: AppProvisioningStateSchema,
    failure: AppProvisioningFailureSchema.nullable(),
    detail: z.string().nullable(),
    attempts: z.int().min(0),
    nextAttemptAt: IsoDateTimeSchema.nullable(),
})
    .openapi('TitleProvisioning', { description: 'Where an app stands in a station’s player account, as the station last reported it (F-CAT-09).' });
// ---------------------------------------------------------------------------------------------
// PUT /v1/station/installed
// ---------------------------------------------------------------------------------------------
/** One title the station found installed (or updating) — a venue library entry of its bundle. */
export const StationInstalledTitleSchema = z
    .object({
    entryId: LibraryEntryIdSchema,
    /**
     * Present while the launcher is updating it; the kiosk shows "Updating {p}%" and holds Play. Optional, not
     * nullable: the station omits a null field on the wire (StationJson), so absence means "not updating".
     */
    updating: InstallUpdatingSchema.optional(),
    /** The installed version where the source records one: Steam `buildid`, Epic `AppVersionString`, a registry `DisplayVersion`. */
    version: InstallVersionSchema.optional(),
    /**
     * True when the station could not check the title (a launcher URI it has no rule for, a launcher present but
     * naming the title differently) and counts it as here so the kiosk never hides it. The console shows such a
     * title as "unverified" rather than installed. Optional: absent means it was found.
     */
    unverified: z.boolean().optional(),
    /** Its size on disk in bytes, where the launcher records one (Steam `SizeOnDisk`, Epic `InstallSize`). Optional. */
    sizeBytes: InstallSizeBytesSchema.optional(),
})
    .openapi('StationInstalledTitle', { description: 'A library title installed on the station, and whether it is updating (F-CAT-03).' });
export const StationInstalledReportSchema = z
    .object({
    /** When the station finished the scan, RFC 3339 UTC. */
    scannedAt: IsoDateTimeSchema,
    /** The library version the scan was made against; absent before the station holds one. */
    libraryVersion: z.string().min(1).max(64).optional(),
    /**
     * The library entries installed (or updating) here. An entry the station cannot probe (a launcher
     * URI it has no rule for) counts as installed, so detection never hides a title it cannot see.
     */
    installed: z.array(StationInstalledTitleSchema).max(MAX_INSTALLED_REPORT_ENTRIES),
    /** The game launchers installed on the PC (Steam, Epic, Riot Client, …), for the kiosk's Apps screen. */
    launchers: z.array(LaunchRecipeLauncherSchema).max(16),
    /**
     * Titles installed on the PC that no library entry matched — every Steam app manifest, Epic manifest and
     * Ubisoft / EA / Battle.net uninstall entry the scan could list cheaply — for "Detected, not in library".
     * Optional: an older station sends none.
     */
    unmatched: z.array(UnmatchedInstallSchema).max(MAX_UNMATCHED_INSTALLS).optional(),
    /**
     * Where each library app with a provisioning recipe stands in the player account (F-CAT-09, ADR-0045). Optional: an
     * older station sends none, and the console then shows no provisioning state.
     */
    provisioning: z.array(StationAppProvisioningSchema).max(MAX_INSTALLED_REPORT_ENTRIES).optional(),
})
    .openapi('StationInstalledReport', {
    description: 'What a station found installed from its library, sent when the scan result changes (F-CAT-03, spec/10 §7.2).',
});
export const putStationInstalledRoute = createRoute({
    method: 'put',
    path: '/v1/station/installed',
    operationId: 'putStationInstalled',
    tags: ['stations', 'library'],
    summary: 'The station reports which library titles are installed on it',
    description: `${STATION_AUTHENTICATION} Replaces the server's record of what is installed on the calling station — the ` +
        'library entries installed or updating, the launchers present (F-CAT-03, spec/10 §7.2) and where each app with a ' +
        'provisioning recipe stands in the player account (F-CAT-09). Sent only when the ' +
        'scan result changes, never as a heartbeat. Entry ids that are not in the venue library are ignored.',
    request: { body: { content: { [JSON_CONTENT_TYPE]: { schema: StationInstalledReportSchema } }, required: true } },
    responses: {
        204: { description: 'The report was stored.' },
        400: commonProblemResponses[400],
        401: problemResponse('The caller is not an authenticated station.', 'unauthenticated'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
// ---------------------------------------------------------------------------------------------
// GET /v1/station/library/availability
// ---------------------------------------------------------------------------------------------
/** One title and the other stations of the venue that report it installed. */
export const StationLibraryAvailabilityEntrySchema = z
    .object({
    entryId: LibraryEntryIdSchema,
    /** The other stations' display names ("PC 4", or the station's own name), by number. Never this station. */
    stations: z.array(z.string()),
})
    .openapi('StationLibraryAvailabilityEntry', { description: 'A library title and the other stations it is installed on (F-CAT-03).' });
export const StationLibraryAvailabilitySchema = z
    .object({
    /** Only titles installed on at least one *other* station of the venue appear. */
    entries: z.array(StationLibraryAvailabilityEntrySchema),
})
    .openapi('StationLibraryAvailability', {
    description: 'Which other stations of the calling station’s venue have each library title installed (F-CAT-03, spec/12 §6).',
});
export const getStationLibraryAvailabilityRoute = createRoute({
    method: 'get',
    path: '/v1/station/library/availability',
    operationId: 'getStationLibraryAvailability',
    tags: ['stations', 'library'],
    summary: 'Read which other stations of the venue have each library title',
    description: `${STATION_AUTHENTICATION} For each library title installed on at least one *other* station of the calling ` +
        'station’s venue, those stations’ names — so the kiosk can show a title not installed here as "On PC 4 and PC 12" ' +
        '(F-CAT-03, spec/12 §6). Scoped to the station’s own venue: never another venue’s stations. Pulled with the library.',
    responses: {
        200: jsonResponse(StationLibraryAvailabilitySchema, 'The other stations each title is installed on.'),
        401: problemResponse('The caller is not an authenticated station.', 'unauthenticated'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
// ---------------------------------------------------------------------------------------------
// GET /v1/organizations/{o}/venues/{v}/stations/{stationId}/installed
// ---------------------------------------------------------------------------------------------
export const StationInstalledParamsSchema = z
    .object({ organizationId: OrganizationIdSchema, venueId: VenueIdSchema, stationId: StationIdSchema })
    .openapi('StationInstalledParams');
/** One title of the station's library, with whether the station reports it installed. */
export const StationLibraryTitleStateSchema = z
    .object({
    entryId: LibraryEntryIdSchema,
    name: z.string(),
    installed: z.boolean(),
    updating: InstallUpdatingSchema.nullable(),
    /** The installed version the station reported, or null. */
    version: z.string().nullable(),
    /** The station counted it as installed without being able to check it (see `StationInstalledTitle.unverified`). */
    unverified: z.boolean(),
    /** Its size on disk in bytes as the station reported it, or null. */
    sizeBytes: z.int().nullable(),
    /** The venue turned Install for players on for it (F-CAT-09). */
    installForPlayers: z.boolean(),
    /**
     * The catalogue app has a provisioning recipe (F-CAT-09), so it can be installed on this PC on demand (ADR-0056,
     * `POST …/stations/{s}/apps/{entryId}/install`) — whether or not Install for players is on.
     */
    provisionable: z.boolean(),
    /** Where it stands in the station's player account (F-CAT-09), or null — no recipe, or nothing reported. */
    provisioning: TitleProvisioningSchema.nullable(),
})
    .openapi('StationLibraryTitleState', { description: 'A title the station’s class is offered, and whether it is installed there (F-CAT-03).' });
export const StationInstalledTitlesSchema = z
    .object({
    /** When the station last reported, or null when it never has (then nothing is known to be installed). */
    scannedAt: IsoDateTimeSchema.nullable(),
    /** How many of `titles` the station reports installed — the "N" of "Installed: N of M". */
    installedCount: z.int().min(0),
    /** The library titles offered to the station's class, installed first, then by name — "M" is its length. */
    titles: z.array(StationLibraryTitleStateSchema),
    /** The game launchers the station reported present. */
    launchers: z.array(LaunchRecipeLauncherSchema),
})
    .openapi('StationInstalledTitles', { description: 'Which of its library titles a station has installed (F-CAT-03, spec/13 §3).' });
export const getStationInstalledTitlesRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/stations/{stationId}/installed',
    operationId: 'getStationInstalledTitles',
    tags: ['stations', 'library'],
    summary: 'Which library titles a station has installed',
    description: 'The venue library titles offered to the station’s class, each marked installed (or updating) as the station last ' +
        'reported it, plus the launchers it found (F-CAT-03, spec/13 §3). A station that never reported shows none installed. ' +
        'Requires `library:read`.',
    [PERMISSION_EXTENSION]: LIBRARY_READ,
    request: { params: StationInstalledParamsSchema },
    responses: {
        200: jsonResponse(StationInstalledTitlesSchema, 'The station’s library titles and their install state.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: problemResponse('Your role does not allow this.', 'forbidden', 'second-factor-required'),
        404: problemResponse('No such station at this venue.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
// ---------------------------------------------------------------------------------------------
// GET /v1/organizations/{o}/venues/{v}/library/installs — the titles × stations matrix
// ---------------------------------------------------------------------------------------------
const forbidden = problemResponse('Your role does not allow this.', 'forbidden', 'second-factor-required');
const venueNotFound = problemResponse('No such venue in this organisation, or not one you can see.', 'not-found');
/** A station class the matrix groups its columns by. */
export const InstallMatrixClassSchema = z
    .object({ classId: StationClassIdSchema, name: z.string() })
    .openapi('InstallMatrixClass', { description: 'A station class of the install matrix (F-CAT-03).' });
/** A zone the matrix can be filtered by (F-CAT-03). */
export const InstallMatrixZoneSchema = z
    .object({ zoneId: ZoneIdSchema, name: z.string() })
    .openapi('InstallMatrixZone', { description: 'A zone of the install matrix, to filter its stations by (F-CAT-03).' });
/** One station column of the matrix: its identity, class, zone and when it last reported. */
export const InstallMatrixStationSchema = z
    .object({
    stationId: StationIdSchema,
    number: z.int(),
    name: z.string().nullable(),
    classId: StationClassIdSchema,
    /** The zone the station stands in, or null when it is in none — the matrix's zone filter. */
    zoneId: ZoneIdSchema.nullable(),
    /** When the station last reported, or null: never — its cells read "not reported". */
    scannedAt: IsoDateTimeSchema.nullable(),
    launchers: z.array(LaunchRecipeLauncherSchema),
})
    .openapi('InstallMatrixStation', { description: 'A station column of the install matrix (F-CAT-03).' });
/** One title row of the matrix. */
export const InstallMatrixTitleSchema = z
    .object({
    entryId: LibraryEntryIdSchema,
    name: z.string(),
    /** The classes it is offered on; empty means every class. A station of another class shows no cell. */
    classIds: z.array(StationClassIdSchema),
    /** The catalogue app has a provisioning recipe, so Install for players can be turned on (F-CAT-09). */
    provisionable: z.boolean(),
    /** The venue turned Install for players on for it (F-CAT-09). */
    installForPlayers: z.boolean(),
})
    .openapi('InstallMatrixTitle', { description: 'A library title row of the install matrix (F-CAT-03).' });
/** A title a station reports installed (or updating). A reported station with no cell for a title is missing it. */
export const InstallMatrixCellSchema = z
    .object({
    stationId: StationIdSchema,
    entryId: LibraryEntryIdSchema,
    updating: InstallUpdatingSchema.nullable(),
    version: z.string().nullable(),
    /** The station counted it as installed without being able to check it: the console shows "unverified". */
    unverified: z.boolean(),
    /** Its size on disk in bytes from the station's latest report, or null when the launcher records none. */
    sizeBytes: z.int().nullable(),
})
    .openapi('InstallMatrixCell', { description: 'A library title installed on a station, as the station last reported it (F-CAT-03).' });
/** Where an app stands in one station's player account (F-CAT-09) — sparse: only what a station reported. */
export const InstallMatrixProvisioningSchema = z
    .object({
    stationId: StationIdSchema,
    entryId: LibraryEntryIdSchema,
    state: AppProvisioningStateSchema,
    failure: AppProvisioningFailureSchema.nullable(),
    detail: z.string().nullable(),
    attempts: z.int().min(0),
    nextAttemptAt: IsoDateTimeSchema.nullable(),
})
    .openapi('InstallMatrixProvisioning', { description: 'An app’s provisioning state on a station, as the station last reported it (F-CAT-09).' });
export const LibraryInstallMatrixSchema = z
    .object({
    classes: z.array(InstallMatrixClassSchema),
    /** The venue's zones, by name — to filter the stations by. */
    zones: z.array(InstallMatrixZoneSchema),
    /** The venue's stations, by class then number. */
    stations: z.array(InstallMatrixStationSchema),
    /** The venue's library titles, by name. */
    titles: z.array(InstallMatrixTitleSchema),
    /** One per installed (or updating) title per station — sparse: the rest are missing, or not reported. */
    cells: z.array(InstallMatrixCellSchema),
    /** One per app with a provisioning state per station (F-CAT-09) — sparse, like `cells`. */
    provisioning: z.array(InstallMatrixProvisioningSchema),
})
    .openapi('LibraryInstallMatrix', { description: 'Which library titles each station of the venue has installed, in one read (F-CAT-03, spec/13 §9.2).' });
export const getLibraryInstallMatrixRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/library/installs',
    operationId: 'getLibraryInstallMatrix',
    tags: ['library'],
    summary: 'The titles × stations install matrix',
    description: 'Every library title against every station of the venue, from each station’s last install report: the installed ' +
        '(or updating) cells with their versions, and each station’s scan time — one read, not one per station ' +
        '(F-CAT-03, spec/13 §9.2). Requires `library:read`.',
    [PERMISSION_EXTENSION]: LIBRARY_READ,
    request: { params: VenueScopeParamsSchema },
    responses: {
        200: jsonResponse(LibraryInstallMatrixSchema, 'The install matrix.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        404: venueNotFound,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
// ---------------------------------------------------------------------------------------------
// GET /v1/organizations/{o}/venues/{v}/library/detected — found on PCs, not in the library
// ---------------------------------------------------------------------------------------------
export const DetectedTitleSchema = z
    .object({
    source: InstallSourceSchema,
    sourceId: z.string(),
    name: z.string(),
    /** The versions seen across the PCs (usually one). */
    versions: z.array(z.string()),
    /** The stations it was found on, by number ("PC 4"). Its length is "found on N PCs". */
    stations: z.array(z.string()),
    /**
     * The platform-catalogue game it matches, or null — "Not in the catalogue yet": a Steam title by a recipe
     * launching `steam://rungameid/<appid>`, else any title by its exact name.
     */
    catalogueGameId: GameIdSchema.nullable(),
    /** The matched catalogue game is already in the venue's library (a recipe the PC's install does not match). */
    inLibrary: z.boolean(),
})
    .openapi('DetectedTitle', { description: 'A title installed on the venue’s PCs that its library does not offer (F-CAT-03).' });
export const DetectedTitlesSchema = z
    .object({ titles: z.array(DetectedTitleSchema) })
    .openapi('DetectedTitles', { description: 'Titles found on the venue’s PCs that no library entry matches, grouped by title (F-CAT-03, spec/13 §9.2).' });
export const listDetectedTitlesRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/library/detected',
    operationId: 'listDetectedTitles',
    tags: ['library'],
    summary: 'Titles found on the venue’s PCs, not in its library',
    description: 'The titles the venue’s stations reported installed that no library entry matched — Steam app manifests, Epic ' +
        'manifests and Ubisoft / EA / Battle.net uninstall entries — grouped by title, most PCs first, each with the ' +
        'catalogue game it matches (if any) so it can be added in one click (F-CAT-03, spec/13 §9.2). Requires `library:read`.',
    [PERMISSION_EXTENSION]: LIBRARY_READ,
    request: { params: VenueScopeParamsSchema },
    responses: {
        200: jsonResponse(DetectedTitlesSchema, 'The detected titles.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        404: venueNotFound,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
// ---------------------------------------------------------------------------------------------
// POST /v1/organizations/{o}/venues/{v}/library/rescan — `station.library.rescan`
// ---------------------------------------------------------------------------------------------
export const LibraryRescanRequestSchema = z
    .object({
    /** The stations to rescan; absent means every station of the venue ("Rescan all"). */
    stationIds: z.array(StationIdSchema).min(1).max(500).optional(),
})
    .openapi('LibraryRescanRequest', { description: 'Which stations to ask to rescan their installs now (F-CAT-03).' });
export const LibraryRescanSchema = z
    .object({ commands: z.array(StationCommandViewSchema) })
    .openapi('LibraryRescan', { description: 'The `station.library.rescan` commands issued, one per station (F-CAT-03).' });
export const rescanLibraryInstallsRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/library/rescan',
    operationId: 'rescanLibraryInstalls',
    tags: ['library', 'stations'],
    summary: 'Ask stations to rescan what is installed now',
    description: 'Issues one `station.library.rescan` command per station — the stations named, or every station of the venue — ' +
        'through the gateway (spec/09 §6.2). Each station scans at once, reports, and acks; the console sees the new ' +
        'scan time on its next read. A station of another venue named here is `404`. Requires `stations:manage`. Audited.',
    [PERMISSION_EXTENSION]: STATIONS_MANAGE,
    request: {
        params: VenueScopeParamsSchema,
        body: { content: { [JSON_CONTENT_TYPE]: { schema: LibraryRescanRequestSchema } }, required: true },
    },
    responses: {
        202: jsonResponse(LibraryRescanSchema, 'The commands issued.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        404: problemResponse('No such venue, or a station that is not this venue’s.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
// ---------------------------------------------------------------------------------------------
// Title requests to HQ (F-CAT-03 rule 4, F-CAT-08): a detected title with no catalogue match
// ---------------------------------------------------------------------------------------------
//
//   POST /v1/organizations/{o}/venues/{v}/library/requests   library:manage   ask HQ to add a detected title
//   GET  /v1/hq/catalogue/requests                           hq:catalog       HQ's inbox, open first
//   POST /v1/hq/catalogue/requests/{requestId}/resolve       hq:catalog       added (with the game) or dismissed
//
// One request per detected title (`source` + `sourceId`) across the Platform: a second venue asking for the same title
// counts on the same request (`requestCount`) rather than opening another, and a dismissed one is reopened. The
// request carries no organisation's data beyond which venue first asked — it is addressed to HQ.
export const CATALOGUE_REQUEST_STATUSES = ['open', 'added', 'dismissed'];
export const CatalogueRequestStatusSchema = z.enum(CATALOGUE_REQUEST_STATUSES).openapi('CatalogueRequestStatus', {
    description: '`open` until HQ answers it; `added` once the title is in the catalogue; `dismissed` when HQ will not add it.',
});
/** A request to HQ to add a title the venues' PCs have but the catalogue does not (F-CAT-08). */
export const CatalogueRequestSchema = z
    .object({
    id: CatalogueRequestIdSchema,
    source: InstallSourceSchema,
    sourceId: z.string(),
    name: z.string(),
    status: CatalogueRequestStatusSchema,
    /** How many times venues asked for it (the first venue and every later one). */
    requestCount: z.int().positive(),
    /** The catalogue game HQ added for it, once `added`; otherwise null. */
    gameId: GameIdSchema.nullable(),
    createdAt: IsoDateTimeSchema,
    /** When a venue last asked for it. */
    lastRequestedAt: IsoDateTimeSchema,
    resolvedAt: IsoDateTimeSchema.nullable(),
})
    .openapi('CatalogueRequest', { description: 'A request to HQ to add a detected title to the platform catalogue (F-CAT-08).' });
export const CreateCatalogueRequestSchema = z
    .object({
    source: InstallSourceSchema,
    sourceId: z.string().trim().min(1).max(128),
    name: z.string().trim().min(1).max(200),
})
    .strict()
    .openapi('CreateCatalogueRequest', { description: 'Ask HQ to add a title found on the venue’s PCs to the catalogue (F-CAT-08).' });
export const CatalogueRequestListSchema = z
    .object({ data: z.array(CatalogueRequestSchema) })
    .openapi('CatalogueRequestList', { description: 'HQ’s title requests: open first, then the most recently asked (F-CAT-08).' });
export const CatalogueRequestListQuerySchema = z
    .object({
    status: CatalogueRequestStatusSchema.optional().openapi({ description: 'Only requests with this status. Absent: every status.' }),
})
    .openapi('CatalogueRequestListQuery');
export const ResolveCatalogueRequestSchema = z
    .object({
    status: z.enum(['added', 'dismissed']),
    /** The catalogue game that answers it — required when `added`. */
    gameId: GameIdSchema.optional(),
})
    .strict()
    .refine((body) => body.status !== 'added' || body.gameId !== undefined, { message: 'An added request names its game.', path: ['gameId'] })
    .openapi('ResolveCatalogueRequest', { description: 'Answer a title request: added (naming the game) or dismissed (F-CAT-08).' });
export const CatalogueRequestParamsSchema = z.object({ requestId: CatalogueRequestIdSchema }).openapi('CatalogueRequestParams');
export const createCatalogueRequestRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/library/requests',
    operationId: 'createCatalogueRequest',
    tags: ['library'],
    summary: 'Ask HQ to add a detected title to the catalogue',
    description: 'For a title under "Detected, not in library" that matches no catalogue title: asks HQ to add it (F-CAT-03, ' +
        'F-CAT-08). Idempotent per title — asking again, from this venue or another, counts on the same request (and ' +
        'reopens a dismissed one). Requires `library:manage`. Audited in the venue’s log (`catalog.request.create`).',
    [PERMISSION_EXTENSION]: LIBRARY_MANAGE,
    request: { params: VenueScopeParamsSchema, body: { content: { [JSON_CONTENT_TYPE]: { schema: CreateCatalogueRequestSchema } }, required: true } },
    responses: {
        200: jsonResponse(CatalogueRequestSchema, 'The request as HQ now holds it.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        404: venueNotFound,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
const hqForbidden = problemResponse('Not HQ, no `hq:catalog`, or no second factor on the session.', 'forbidden', 'second-factor-required');
export const listCatalogueRequestsRoute = createRoute({
    method: 'get',
    path: '/v1/hq/catalogue/requests',
    operationId: 'listCatalogueRequests',
    tags: ['hq'],
    summary: 'HQ’s inbox of title requests',
    description: 'The titles venues asked HQ to add (F-CAT-08): open first, then the most recently asked, at most 500. Platform ' +
        'scope: requires `hq:catalog` and a second factor.',
    [PERMISSION_EXTENSION]: HQ_CATALOG,
    request: { query: CatalogueRequestListQuerySchema },
    responses: {
        200: jsonResponse(CatalogueRequestListSchema, 'The title requests.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: hqForbidden,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const resolveCatalogueRequestRoute = createRoute({
    method: 'post',
    path: '/v1/hq/catalogue/requests/{requestId}/resolve',
    operationId: 'resolveCatalogueRequest',
    tags: ['hq'],
    summary: 'Answer a title request',
    description: 'Marks the request `added` (naming the catalogue game that answers it) or `dismissed` (F-CAT-08). A game id the ' +
        'catalogue does not hold is `422`. Audited (`catalog.request.resolve`). Platform scope: requires `hq:catalog` and ' +
        'a second factor.',
    [PERMISSION_EXTENSION]: HQ_CATALOG,
    request: { params: CatalogueRequestParamsSchema, body: { content: { [JSON_CONTENT_TYPE]: { schema: ResolveCatalogueRequestSchema } }, required: true } },
    responses: {
        200: jsonResponse(CatalogueRequestSchema, 'The request as it is now.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: hqForbidden,
        404: problemResponse('No such title request.', 'not-found'),
        422: problemResponse('The game named is not in the catalogue.', 'unprocessable'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
// ---------------------------------------------------------------------------------------------
// Install one app on demand (F-CAT-09, ADR-0056): `station.app.install` to one PC, or to every PC missing it
// ---------------------------------------------------------------------------------------------
//
// An app of the venue library with a provisioning recipe can be installed on a PC now, as a one-off, without turning
// Install for players on for the whole venue: the PC installs it into the account players use as soon as it is free (on
// the lock screen, at once; never during a session), exactly as Install for players would. A PC that is busy or offline
// gets it at its next free moment (the command waits up to seven days for it to connect). `library:manage`: the people who
// may already have every PC install the app with the switch. Each request is audited.
/**
 * The `Idempotency-Key` the install requests accept: a double-click asks once. A retry with the same key and body is
 * answered from the first answer (`Idempotent-Replayed: true`); the same key with another body is
 * `422 idempotency-key-reused`. Optional, so a client that sends none still asks (the station keeps one request per app).
 */
export const AppInstallIdempotencyHeaderSchema = z
    .object({ 'idempotency-key': IdempotencyKeySchema.optional() })
    .openapi('AppInstallIdempotencyHeaders');
export const StationAppInstallParamsSchema = z
    .object({ organizationId: OrganizationIdSchema, venueId: VenueIdSchema, stationId: StationIdSchema, entryId: LibraryEntryIdSchema })
    .openapi('StationAppInstallParams');
export const installStationAppRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/stations/{stationId}/apps/{entryId}/install',
    operationId: 'installStationApp',
    tags: ['library', 'stations'],
    summary: 'Install one library app on one station',
    description: 'Ask the station to install this library app into the account players use as soon as it is free (on the lock screen, at ' +
        'once; never during a session) as a one-off: Install for players stays as it is (F-CAT-09, ADR-0056). Issued as ' +
        '`station.app.install` through the gateway; answers `202` with the command, which the console follows over the member’s ' +
        '`command.updated` (`StationAppInstallCommandResult`). A station that is offline gets it when it next connects, within ' +
        'seven days; meanwhile the app reads `queued` for it. `422` for an entry whose catalogue app has no provisioning recipe; ' +
        '`409` for an entry hidden from the PCs, a station that has no station app (run by staff, Clavian Counter, not paired ' +
        'yet), one whose class the entry is not offered to, one that last reported the app installed for players, or one that ' +
        'cannot install it (a diskless client, a Store app staged on no account of the PC). Requires `library:manage`. Audited ' +
        '(`catalog.library.install_requested`).',
    [PERMISSION_EXTENSION]: LIBRARY_MANAGE,
    request: { params: StationAppInstallParamsSchema, headers: AppInstallIdempotencyHeaderSchema },
    responses: {
        202: jsonResponse(StationCommandViewSchema, 'The command was written and published.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        404: problemResponse('No such station at this venue, or no such entry in its library.', 'not-found'),
        409: problemResponse('This station cannot be asked to install the app: see the detail.', 'conflict'),
        422: problemResponse('The app has no provisioning recipe, or the key was used for a different request.', 'unprocessable', 'idempotency-key-reused'),
        429: commonProblemResponses[429],
        500: problemResponse('The realtime gateway is not available to deliver commands.', 'internal-error'),
    },
});
export const LibraryEntryInstallParamsSchema = z
    .object({ organizationId: OrganizationIdSchema, venueId: VenueIdSchema, entryId: LibraryEntryIdSchema })
    .openapi('LibraryEntryInstallParams');
/** Why a PC of the venue was not asked to install the app (`LibraryEntryInstall.skipped`). */
export const APP_INSTALL_SKIP_REASONS = ['installed', 'cannot_install'];
export const AppInstallSkipReasonSchema = z.enum(APP_INSTALL_SKIP_REASONS).openapi('AppInstallSkipReason', {
    description: 'Why a PC was not asked: `installed` (its last report has the app installed for players) or `cannot_install` (a diskless ' +
        'client, which installs it on the image, or a Store app staged on no account of the PC).',
});
export const AppInstallSkippedStationSchema = z
    .object({ stationId: StationIdSchema, reason: AppInstallSkipReasonSchema })
    .openapi('AppInstallSkippedStation', { description: 'A PC that was not asked to install the app, and why (ADR-0056).' });
export const LibraryEntryInstallSchema = z
    .object({
    /** One `station.app.install` per PC asked: the venue's PCs the entry is offered to that are missing the app. */
    commands: z.array(StationCommandViewSchema),
    /** The PCs it is offered to that were not asked, and why. */
    skipped: z.array(AppInstallSkippedStationSchema),
})
    .openapi('LibraryEntryInstall', { description: 'Which PCs were asked to install the app, and which were not and why (F-CAT-09, ADR-0056).' });
export const installLibraryEntryRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/library/{entryId}/install',
    operationId: 'installLibraryEntry',
    tags: ['library', 'stations'],
    summary: 'Install one library app on every PC missing it',
    description: 'Ask every PC of the venue the entry is offered to (its station classes; every class when none) that is missing the app ' +
        'in the account players use to install it as soon as it is free (never during a session) as a one-off: Install for ' +
        'players stays as it is (F-CAT-09, ADR-0056). One `station.app.install` per PC; a PC whose last report has the app ' +
        'installed for players, or that cannot install it (a diskless client, a Store app staged on no account of the PC), is ' +
        'not asked and is listed in `skipped`; a PC that has not reported yet is asked. Stations run by staff (no station app), ' +
        'Clavian Counter and PCs not paired yet are left out. Offline PCs get it when they next connect, within seven days. `422` ' +
        'for an entry whose catalogue app has no provisioning recipe; `409` for an entry hidden from the PCs (they could not ' +
        'install what their library does not hold). Requires `library:manage`. Audited once (`catalog.library.install_requested`, ' +
        'with the PCs asked).',
    [PERMISSION_EXTENSION]: LIBRARY_MANAGE,
    request: { params: LibraryEntryInstallParamsSchema, headers: AppInstallIdempotencyHeaderSchema },
    responses: {
        202: jsonResponse(LibraryEntryInstallSchema, 'The commands issued, and the PCs not asked.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        404: problemResponse('No such venue, or no such entry in its library.', 'not-found'),
        409: problemResponse('The entry is hidden from the PCs.', 'conflict'),
        422: problemResponse('The app has no provisioning recipe, or the key was used for a different request.', 'unprocessable', 'idempotency-key-reused'),
        429: commonProblemResponses[429],
        500: problemResponse('The realtime gateway is not available to deliver commands.', 'internal-error'),
    },
});
export const STATION_INSTALL_ROUTES = [
    putStationInstalledRoute,
    getStationLibraryAvailabilityRoute,
    getStationInstalledTitlesRoute,
    getLibraryInstallMatrixRoute,
    listDetectedTitlesRoute,
    rescanLibraryInstallsRoute,
    installStationAppRoute,
    installLibraryEntryRoute,
    createCatalogueRequestRoute,
    listCatalogueRequestsRoute,
    resolveCatalogueRequestRoute,
];
//# sourceMappingURL=station-installs.js.map